import { FC, useMemo } from "react";
import isEqual from "lodash/isEqual";
import { useTranslation } from "react-i18next";

import styles from "./KeywordsControl.module.scss";
import { Keyword } from "src/features/KeywordsTable/types";
import { InputWithIcon, Tabs, Select } from "src/components";
import { KeywordsTab } from "../../types";

type KeywordFilterMode = "exact" | "all";

type Props = {
  isKeywordsLoading: boolean;
  isKeywordsPending: boolean;
  keywordsSearch: string;
  selectedKeywords: Record<Keyword, true>;
  setKeywordsSearch: (value: string) => void;
  autoSelectedKeywords: Record<Keyword, true> | null;
  activeTab: KeywordsTab;
  setActiveTab: (value: string) => void;
  keywordsSelectLoadingStatus: LoadingStatus;
  selectRecommendedKeywordsHandler: () => void;
  keywordFilterMode: KeywordFilterMode;
  setKeywordFilterMode: (mode: KeywordFilterMode) => void;
};

export const KeywordsControl: FC<Props> = ({
  keywordsSearch,
  selectedKeywords,
  setKeywordsSearch,
  isKeywordsLoading,
  isKeywordsPending,
  activeTab,
  setActiveTab,
  autoSelectedKeywords,
  keywordsSelectLoadingStatus,
  selectRecommendedKeywordsHandler,
  keywordFilterMode,
  setKeywordFilterMode,
}) => {
  const { t } = useTranslation();

  const isSelectLoading = useMemo<boolean>(
    () => keywordsSelectLoadingStatus === "loading",
    [keywordsSelectLoadingStatus],
  );

  const hasKeywordsAutoSelected = useMemo<boolean>(
    () =>
      Boolean(autoSelectedKeywords) &&
      isEqual(selectedKeywords, autoSelectedKeywords),
    [autoSelectedKeywords, selectedKeywords],
  );

  const isSelectDisabled = useMemo<boolean>(
    () => isSelectLoading || hasKeywordsAutoSelected,
    [isSelectLoading, hasKeywordsAutoSelected],
  );

  const isActionButtonsShown = useMemo<boolean>(
    () => !isKeywordsPending && !isKeywordsLoading,
    [isKeywordsLoading, isKeywordsPending],
  );

  const onSmartSelectClick = (): void => selectRecommendedKeywordsHandler();

  const keywordFilterOptions = useMemo(
    () => [
      {
        value: "exact",
        label: t("component.keywords_table.filter.exact_match"),
        description: "component.keywords_table.filter.exact_match.description",
      },
      {
        value: "all",
        label: t("component.keywords_table.filter.all_keywords"),
        description: "component.keywords_table.filter.all_keywords.description",
      },
    ],
    [t],
  );

  return (
    <div className={styles.wrapper}>
      <InputWithIcon
        value={keywordsSearch}
        changeHandler={setKeywordsSearch}
        className={styles.keywordsSearchInput}
        icon="Search"
        hasClearButton
      />
      {isActionButtonsShown && (
        <>
          <Select
            selectClassName={styles.keywordFilterSelect}
            options={keywordFilterOptions}
            value={keywordFilterMode}
            changeHandler={(value) => setKeywordFilterMode(value as KeywordFilterMode)}
          />
          <div className={styles.buttonsWrapper}>
            <Tabs
              className={styles.tabs}
              activeTab={activeTab}
              tabs={[
                {
                  value: "all",
                  label: t("component.keywords_table.tab.all"),
                  onSelect: setActiveTab,
                },
                {
                  value: "selected",
                  label: t("component.keywords_table.tab.selected"),
                  onSelect: setActiveTab,
                },
                {
                  value: "unselected",
                  label: t("component.keywords_table.tab.unselected"),
                  onSelect: setActiveTab,
                },
                {
                  value: "new",
                  label: t("component.keywords_table.tab.new"),
                  onSelect: setActiveTab,
                },
                {
                  value: "duplicates",
                  label: t("component.keywords_table.tab.duplicates"),
                  onSelect: setActiveTab,
                },
              ]}
            />
          </div>
        </>
      )}
      {/* {isActionButtonsShown && (
        <Button
          buttonType="dark"
          buttonSize="small"
          disabled={isSelectDisabled}
          onClick={onSmartSelectClick}
          className={styles.smartSelectButton}
        >
          <span>{t("component.keywords_table.button.smart_select")}</span>
          {isSelectLoading && <Loader className={styles.loader} />}
        </Button>
      )} */}
    </div>
  );
};
